import { create } from "zustand";

type ThreadMetadata = Record<string, string> | null;

export type Thread = {
  id: string;
  title: string;
  metadata: ThreadMetadata;
  updatedAt: string | Date | null;
  createdAt: string | Date;
  pinned: boolean;
};

interface ThreadState {
  threads: Thread[];
  lastThread: string | null;
  setLastThread: (id: string) => void;
  setThreads: (threads: Thread[]) => void;
  addThread: (thread: Thread) => void;
  updateThread: (id: string, data: Partial<Omit<Thread, "id">>) => void;
  renameThread: (data: { title: string; id: string }) => void;
  removeThread: (id: string) => void;
}

export const useThreadsStore = create<ThreadState>((set) => ({
  threads: [],
  lastThread: null,
  setLastThread: (id) =>
    set(() => ({
      lastThread: id,
    })),
  addThread: (thread) =>
    set((state) => ({
      threads: [thread, ...state.threads.filter((t) => t.id !== thread.id)],
    })),
  updateThread: (id, data) =>
    set((state) => ({
      threads: state.threads
        .map((thread) => (thread.id === id ? { ...thread, ...data } : thread))
        .sort((a, b) => {
          const aUpdatedAt = new Date(a.updatedAt || 0).getTime();
          const bUpdatedAt = new Date(b.updatedAt || 0).getTime();
          const aCreatedAt = new Date(a.createdAt).getTime();
          const bCreatedAt = new Date(b.createdAt).getTime();
          return bUpdatedAt - aUpdatedAt || bCreatedAt - aCreatedAt;
        }),
    })),
  setThreads: (threads) =>
    set(() => ({
      threads,
    })),
  renameThread: ({ title, id }) =>
    set((state) => ({
      threads: state.threads.map((thread) =>
        thread.id === id ? { ...thread, title } : thread,
      ),
    })),
  removeThread: (id) =>
    set((state) => ({
      threads: state.threads.filter((thread) => thread.id !== id),
    })),
}));
